import Keycloak from 'keycloak-js'
import { ServiceLike, SingletonServices } from 'wdc-cube'
import { Logger } from 'wdc-cube'

import Hash from 'hash.js'
import { HmacDRBG } from 'hmac-drbg'
import esaLong from 'rsa-long'
import { AppStorage, ServiceClient } from 'src/utils'
import { v4 as uuid } from 'uuid'

const LOG = Logger.get('AuthenticationService')

// KeyGen: https://cryptotools.net/rsagen

const appStorage = AppStorage.singleton()
const serviceClient = ServiceClient.singleton()
const rememberMeProp = (function () {
    const key = 'WSEGpG'
    return new (class {
        get value(): boolean {
            return 'true' === sessionStorage.getItem(key)
        }

        set value(v: boolean) {
            if (v) {
                sessionStorage.setItem(key, 'true')
            } else {
                sessionStorage.removeItem(key)
            }
        }
    })()
})()

export type SignInResult = {
    accessToken: string
    refreshToken: string
    idToken: string
}

// uso criptografado
const sipPubKey =
    '-----BEGIN PUBLIC KEY-----' +
    'MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQDUxiRooulpsasGO6vMPDe9taRH' +
    'grA6wS3B9wu52x4JNcA42amNq6i9uQk+atWNVJeH7eGVi+Qg3Whd2HVMaV7te3Dc' +
    'xvZqd/foPY6TknZkiA0sbKpLz3ZU1VOcpcQeL+quTzI8blGq91Mv83QxSh8TEfId' +
    'jTT8uY98ExbCahWDkQIDAQAB' +
    '-----END PUBLIC KEY-----'

export class AuthenticationService implements ServiceLike {
    private static INSTANCE = new AuthenticationService()

    public static readonly singleton = () => AuthenticationService.INSTANCE

    private keycloak?: Keycloak

    private provider = 'mc'

    private __initialized = false

    get initialized(): boolean {
        return this.__initialized
    }

    get name(): string {
        return 'AuthenticationService'
    }

    get isKeycloakProvider(): boolean {
        return this.provider === 'mc'
    }

    get isAppProvider(): boolean {
        return !this.isKeycloakProvider
    }

    async postConstruct() {
        this.provider = location.hostname.split('.')[0]

        if (this.isKeycloakProvider && !this.keycloak) {
            const keycloak = (this.keycloak = new Keycloak({
                url:
                    location.hostname === 'mc.localhost'
                        ? 'http://keycloak:9091/auth'
                        : 'https://lgni.grupomultiplica.com.br/auth',
                realm: 'mc',
                clientId: 'mc-client'
            }))

            const updtAct = () =>
                this.updateAccessToken(
                    {
                        accessToken: keycloak!.token!,
                        refreshToken: keycloak!.refreshToken!,
                        idToken: keycloak!.idToken!
                    },
                    true
                )

            keycloak.onAuthSuccess = async () => {
                await this.loadKeycloakUserProfile()
                updtAct()
            }

            keycloak.onAuthRefreshSuccess = updtAct
            keycloak.onTokenExpired = async () => await this.refreshAccessToken(true).catch(LOG.caught)

            await keycloak.init({
                onLoad: 'check-sso',
                refreshToken: appStorage.refreshToken,
                token: appStorage.accessToken,
                idToken: appStorage.idToken,
                flow: 'standard'
            })
        }

        this.__initialized = true
    }

    async preDestroy() {
        this.keycloak = undefined
    }

    private updateAccessToken(body: SignInResult, rememberMe: boolean) {
        appStorage.setAccessToken(
            {
                token: body.accessToken,
                idToken: body.idToken,
                refreshToken: body.refreshToken
            },
            rememberMe
        )
    }

    private async loadKeycloakUserProfile() {
        const usr = await this.keycloak!.loadUserProfile()
        appStorage.setUserName(usr.username!, true)
    }

    async reloadAuthorizations() {
        try {
            await this.refreshAccessToken(rememberMeProp.value)
            return true
        } catch (e) {
            LOG.warn('It was not possible to refresh autorizations', e)
            return false
        }
    }

    async signIn(username: string, password: string, rememberMe: boolean) {
        const entropy = uuid()
        const nonce = uuid()

        const drbg = new HmacDRBG({
            hash: Hash.sha256,
            entropy: entropy,
            entropyEnc: 'utf8',
            nonce: nonce,
            nonceEnc: 'utf8',
            pers: password,
            persEnc: 'utf8'
        })

        const encryptedPassword = esaLong.encryptLong(
            sipPubKey,
            JSON.stringify({
                entropy,
                nonce,
                value: drbg.generate(32, 'hex') as string
            })
        )

        const body = await serviceClient.post<SignInResult>('/api/auth/login', {
            username,
            password: encryptedPassword
        })

        rememberMeProp.value = rememberMe
        appStorage.setUserName(username, rememberMe)

        this.updateAccessToken(body, rememberMe)
    }

    async refreshAccessToken(rememberMe: boolean) {
        const keyCloak = this.keycloak
        if (keyCloak) {
            await keyCloak
                .updateToken(5)
                .then((refreshed) => LOG.info(`Token refreshed: ${refreshed}!`))
                .catch(() => LOG.error('Failed to refresh the token, or the session has expired'))
        } else {
            const body = await serviceClient.authPost<SignInResult>('/api/auth/refresh')
            this.updateAccessToken(body, rememberMe)
        }
    }

    async requestPasswordReset(email: string) {
        if (this.keycloak) {
            throw new Error('Not availabel on keycloak mode')
        } else {
            await serviceClient.post<SignInResult>('/api/auth/reset/password', { email })
        }
    }

    async loginKeycloak() {
        const keycloak = this.keycloak
        if (!keycloak) {
            return false
        }

        let valid = false
        try {
            const result = await serviceClient.authPost<{ valid: boolean }>('/api/auth/check')
            valid = result.valid
        } catch (exn) {
            LOG.caught(exn)
            valid = false
        }

        if (!valid) {
            await keycloak.login()
            return false
        }

        return true
    }

    async logoutKeycloak() {
        if (this.keycloak) {
            await this.keycloak.logout()
        }
    }
}

SingletonServices.add(AuthenticationService.singleton())
